import { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch } from '../../../utils/api';
import { selectableLaunchFiles } from '../../../utils/launchFiles';
import { Button } from '../../design/Button';
import { Select } from '../../design/Select';
import { Field } from './primitives';

interface JavaVersion { path: string; name: string; version: string }
export type LaunchMode = 'default' | 'custom_jar';

interface Props {
  serverId: string;
  launchMode: LaunchMode;
  jarFile: string;
  javaPath: string;
  onChange: (patch: { launch_mode?: LaunchMode; jar_file?: string; java_path?: string }) => void;
}

/** Startart, Startdatei, Java-Version und „Startskript neu generieren" (nur Minecraft Java). */
export const LaunchFields = ({ serverId, launchMode, jarFile, javaPath, onChange }: Props) => {
  const [javaVersions, setJavaVersions] = useState<JavaVersion[]>([]);
  const [loadingJava, setLoadingJava] = useState(true);
  const [files, setFiles] = useState<string[]>([]);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch('/api/servers/java-versions')
      .then((r) => r.json())
      .then((d) => { if (alive && d.success) setJavaVersions(d.versions); })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingJava(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (launchMode !== 'custom_jar') return;
    let alive = true;
    apiFetch(`/api/servers/${serverId}/files`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive || !d.success || !d.files) return;
        // Startbar: JARs und Scripts (.sh/.bat, laufen per bash); Installer fliegen raus.
        const names = selectableLaunchFiles(
          d.files.filter((f: any) => !f.isDirectory && /\.(jar|sh|bat)$/i.test(f.name)).map((f: any) => f.name),
        ).sort((a: string, b: string) => {
          const as = /\.(sh|bat)$/i.test(a), bs = /\.(sh|bat)$/i.test(b);
          return as !== bs ? (as ? -1 : 1) : a.localeCompare(b);
        });
        setFiles(names);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [launchMode, serverId]);

  const regenerate = async () => {
    // Zwei-Klick-Bestätigung: überschreibt ein eigenes start.sh.
    if (!confirmRegen) {
      setConfirmRegen(true);
      setTimeout(() => setConfirmRegen(false), 4000);
      return;
    }
    setConfirmRegen(false);
    setRegenerating(true);
    try {
      const res = await apiFetch(`/api/servers/${serverId}/start-script/regenerate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) toast.success(data.message || 'Startskript neu generiert');
      else toast.error(data.message || 'Konnte kein Startskript generieren');
    } catch {
      toast.error('Netzwerkfehler');
    } finally {
      setRegenerating(false);
    }
  };

  const javaOptions = javaVersions.map((v) => ({ value: v.path, label: v.name, hint: v.version }));
  if (!javaOptions.some((o) => o.value === javaPath)) {
    javaOptions.unshift({ value: javaPath, label: javaPath === 'java' ? 'System-Standard (java)' : javaPath, hint: '' });
  }

  return (
    <>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Startart" hint="Standard findet start.sh / run.sh / server.jar selbst – passt für fast alle Server.">
          <Select
            value={launchMode}
            onChange={(v) => onChange({ launch_mode: v as LaunchMode })}
            options={[
              { value: 'default', label: 'Standard (automatisch)' },
              { value: 'custom_jar', label: 'Eigene Startdatei (.jar / .sh)' },
            ]}
          />
        </Field>
        <Field label="Java-Version" hint="Neuere Minecraft-Versionen brauchen neueres Java (ab 1.20.5: Java 21).">
          {loadingJava
            ? <div className="text-xs text-fg-muted animate-pulse py-2.5">Lade Java-Versionen …</div>
            : <Select value={javaPath} onChange={(v) => onChange({ java_path: v })} options={javaOptions} />}
        </Field>
      </div>

      {launchMode === 'custom_jar' && (
        <Field label="Startdatei" hint="Scripts (.sh/.bat) laufen per bash, .jar mit Java.">
          <Select value={jarFile} onChange={(v) => onChange({ jar_file: v })} placeholder="Wähle .jar oder .sh …" options={files.map((f) => ({ value: f, label: f }))} />
        </Field>
      )}

      <div className="pt-4 border-t border-line flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-fg-muted max-w-md leading-relaxed">
          Startet der Server nicht mehr? Ein frisches, schnelles Startskript passend zum Loader (Aikar-Flags) hilft oft.
          Überschreibt ein eigenes start.sh.
        </p>
        <Button type="button" variant={confirmRegen ? 'danger' : 'outline'} size="sm" disabled={regenerating} onClick={regenerate}>
          {regenerating ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          {confirmRegen ? 'Wirklich überschreiben? Nochmal klicken' : 'Startskript neu generieren'}
        </Button>
      </div>
    </>
  );
};
